// Multipart upload utility for large files to Cloudflare R2
// Enhanced with retry logic and network resilience

export class MultipartUploader {
  constructor(file, onProgress = () => {}) {
    this.file = file
    this.onProgress = onProgress
    this.uploadId = null
    this.key = null
    this.parts = []
    this.aborted = false
    this.activeRequests = new Set()
    this.networkOnline = navigator.onLine
    this.retryCount = 0
    this.maxRetries = 3

    // Import chunk size from constants for consistency
    this.chunkSize = 50 * 1024 * 1024 // 50MB chunks for network stability (TODO: import from constants)
    this.totalChunks = Math.ceil(file.size / this.chunkSize)
    this.uploadedChunks = 0

    // Network monitoring
    this.setupNetworkMonitoring()
  }

  setupNetworkMonitoring() {
    window.addEventListener('online', () => {
      console.log('🌐 Network restored, can resume upload')
      this.networkOnline = true
    })

    window.addEventListener('offline', () => {
      console.log('📡 Network lost, upload will retry when restored')
      this.networkOnline = false
    })
  }

  async start() {
    try {
      console.log(`🚀 Starting multipart upload: ${this.file.name} (${(this.file.size / 1024 / 1024).toFixed(1)}MB, ${this.totalChunks} chunks)`)

      // Show immediate progress feedback
      this.onProgress(1)

      // Step 1: Initialize multipart upload
      console.log('📋 Initializing multipart upload...')
      await this.initializeUpload()
      this.onProgress(5) // Show 5% after initialization

      // Step 2: Upload chunks in parallel (max 3 concurrent)
      console.log('📤 Starting chunk uploads...')
      await this.uploadChunks()

      // Step 3: Complete multipart upload
      console.log('🔄 Completing multipart upload...')
      this.onProgress(95) // Show 95% before completion
      const result = await this.completeUpload()

      console.log(`✅ Multipart upload completed: ${result.location}`)
      return result

    } catch (error) {
      console.error('❌ Multipart upload failed:', error)

      // Clean up on failure
      if (this.uploadId && !this.aborted) {
        await this.abortUpload()
      }

      throw error
    }
  }

  async initializeUpload() {
    const token = localStorage.getItem('token')

    const response = await fetch('/.netlify/functions/r2-multipart-init', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        fileName: this.file.name,
        fileSize: this.file.size,
        contentType: this.file.type,
        metadata: {
          uploadType: 'complete-video',
          totalChunks: this.totalChunks.toString()
        }
      })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to initialize multipart upload')
    }

    const data = await response.json()
    this.uploadId = data.uploadId
    this.key = data.key

    console.log(`📋 Multipart upload initialized: ${this.uploadId}`)
  }

  async uploadChunks() {
    const chunks = []

    // Create chunk metadata
    for (let i = 0; i < this.totalChunks; i++) {
      const start = i * this.chunkSize
      const end = Math.min(start + this.chunkSize, this.file.size)

      chunks.push({
        partNumber: i + 1,
        start,
        end,
        size: end - start
      })
    }

    // Upload chunks with controlled concurrency (max 2 concurrent for stability)
    const maxConcurrent = 2
    const uploadPromises = []

    for (let i = 0; i < chunks.length; i += maxConcurrent) {
      const batch = chunks.slice(i, i + maxConcurrent)
      const batchPromises = batch.map(chunk => this.uploadChunkWithRetry(chunk))

      // Wait for this batch to complete before starting next batch
      const batchResults = await Promise.all(batchPromises)
      uploadPromises.push(...batchResults)

      // Update progress after each batch completes
      const batchProgress = 5 + Math.round((this.uploadedChunks / this.totalChunks) * 85)
      this.onProgress(batchProgress)
      console.log(`📊 Batch complete: ${this.uploadedChunks}/${this.totalChunks} chunks (${batchProgress}%)`)

      // Check if aborted
      if (this.aborted) {
        throw new Error('Upload cancelled')
      }
    }

    console.log(`📦 All ${this.totalChunks} chunks uploaded successfully`)
  }

  async uploadChunkWithRetry(chunk, attemptNumber = 1) {
    if (this.aborted) {
      throw new Error('Upload cancelled')
    }

    // Wait for network if offline - ENHANCED
    await this.waitForNetwork()

    // Additional check after network wait
    if (!navigator.onLine && attemptNumber === 1) {
      console.log('🌐 Waiting for network stability before chunk upload...')
      await new Promise(resolve => setTimeout(resolve, 2000)) // 2sec stability wait
    }

    try {
      return await this.uploadChunk(chunk, attemptNumber)
    } catch (error) {
      console.warn(`⚠️ Upload attempt ${attemptNumber} failed for part ${chunk.partNumber}:`, error.message)

      // Special handling for backend connection issues
      if (error.message.includes('ERR_CONNECTION_REFUSED') || error.message.includes('net::ERR_CONNECTION_REFUSED')) {
        console.error(`🚨 Backend connection refused - Netlify functions may need deployment`)
        console.error(`💡 Solution: Run 'npm run deploy' to update backend functions`)
        console.error(`🔍 Function endpoint failing: ${chunk.partNumber > 1 ? 'r2-multipart-url' : 'r2-multipart-init'}`)
      }

      if (error.message.includes('ERR_NETWORK_IO_SUSPENDED') || error.message.includes('net::ERR_NETWORK_IO_SUSPENDED')) {
        console.error(`📡 Network I/O suspended - connection interrupted`)
        console.error(`🔄 Will retry with network monitoring...`)
      }

      if (error.message.includes('Failed to fetch') && navigator.onLine) {
        console.error(`🌐 Browser online but fetch failed - likely backend function issue`)
        console.error(`💡 Try: Clear browser cache or redeploy backend functions`)
      }

      if (attemptNumber < this.maxRetries) {
        // Check network before retrying
        if (!navigator.onLine) {
          console.log(`📡 Network offline detected, waiting for connection before retry...`)
          await this.waitForNetwork()
        }

        // Exponential backoff: 1s, 2s, 4s
        const delay = Math.pow(2, attemptNumber - 1) * 1000
        console.log(`🔄 Retrying part ${chunk.partNumber} in ${delay}ms (attempt ${attemptNumber + 1}/${this.maxRetries})`)

        await new Promise(resolve => setTimeout(resolve, delay))
        return this.uploadChunkWithRetry(chunk, attemptNumber + 1)
      } else {
        console.error(`💥 Part ${chunk.partNumber} failed after ${this.maxRetries} attempts`)
        console.error(`🔍 Debug info - Last error:`, error.message)
        console.error(`🔍 Navigator online:`, navigator.onLine)
        throw error
      }
    }
  }

  async waitForNetwork() {
    if (!this.networkOnline || !navigator.onLine) {
      console.log('⏳ Waiting for network connection...')
      return new Promise((resolve) => {
        const checkNetwork = () => {
          if (navigator.onLine && this.networkOnline) {
            console.log('🌐 Network connection restored, resuming upload')
            resolve()
          } else {
            console.log('📡 Still offline, checking again in 1s...')
            setTimeout(checkNetwork, 1000)
          }
        }
        checkNetwork()
      })
    }
  }

  async uploadChunk(chunk, attemptNumber = 1) {
    if (this.aborted) {
      throw new Error('Upload cancelled')
    }

    const token = localStorage.getItem('token')

    try {
      console.log(`📤 Uploading part ${chunk.partNumber}/${this.totalChunks} (${(chunk.size / 1024 / 1024).toFixed(1)}MB) - Attempt ${attemptNumber}`)

      // Get signed URL for this chunk
      const urlResponse = await fetch('/.netlify/functions/r2-multipart-url', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          uploadId: this.uploadId,
          partNumber: chunk.partNumber,
          key: this.key
        })
      })

      if (!urlResponse.ok) {
        const error = await urlResponse.json().catch(() => ({}))
        throw new Error(error.error || `Failed to get signed URL for part ${chunk.partNumber}`)
      }

      const { signedUrl } = await urlResponse.json()

      // Extract chunk data
      const chunkBlob = this.file.slice(chunk.start, chunk.end)

      // Upload chunk to R2 with enhanced headers
      const uploadResponse = await fetch(signedUrl, {
        method: 'PUT',
        body: chunkBlob,
        headers: {
          'Content-Type': 'application/octet-stream'
        }
      })

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload part ${chunk.partNumber}: ${uploadResponse.status} ${uploadResponse.statusText}`)
      }

      // Get ETag from response
      const etag = uploadResponse.headers.get('ETag')
      if (!etag) {
        throw new Error(`No ETag received for part ${chunk.partNumber}`)
      }

      // Store part info for completion
      this.parts.push({
        PartNumber: chunk.partNumber,
        ETag: etag
      })

      this.uploadedChunks++
      console.log(`✅ Uploaded part ${chunk.partNumber}/${this.totalChunks} (${(chunk.size / 1024 / 1024).toFixed(1)}MB) - ETag: ${etag.substring(0, 8)}...`)

      // Update progress immediately after each chunk (5% to 90% range)
      const chunkProgress = 5 + Math.round((this.uploadedChunks / this.totalChunks) * 85)
      this.onProgress(chunkProgress)

      return etag

    } catch (error) {
      console.error(`❌ Failed to upload part ${chunk.partNumber} (attempt ${attemptNumber}):`, error)
      throw error
    }
  }

  async completeUpload() {
    if (this.aborted) {
      throw new Error('Upload cancelled')
    }

    const token = localStorage.getItem('token')

    // Sort parts by part number
    const sortedParts = this.parts.sort((a, b) => a.PartNumber - b.PartNumber)

    const response = await fetch('/.netlify/functions/r2-multipart-complete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        uploadId: this.uploadId,
        key: this.key,
        parts: sortedParts
      })
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({}))
      throw new Error(error.error || 'Failed to complete multipart upload')
    }

    const result = await response.json()

    // Update progress to 100%
    this.onProgress(100)

    return {
      success: true,
      location: result.location,
      etag: result.etag,
      key: this.key,
      bucket: result.bucket
    }
  }

  async abortUpload() {
    if (!this.uploadId || this.aborted) return

    this.aborted = true

    // Cancel all active requests
    for (const request of this.activeRequests) {
      if (request.abort) {
        request.abort()
      }
    }
    this.activeRequests.clear()

    try {
      const token = localStorage.getItem('token')

      await fetch('/.netlify/functions/r2-multipart-abort', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          uploadId: this.uploadId,
          key: this.key
        })
      })

      console.log(`🚫 Multipart upload aborted: ${this.uploadId}`)
    } catch (error) {
      console.warn('Failed to abort multipart upload:', error)
    }
  }

  cancel() {
    console.log('🚫 Cancelling multipart upload...')
    this.abortUpload()
  }
}

// Helper function for simple usage
export async function uploadLargeFile(file, onProgress = () => {}) {
  // Use multipart upload for files > 500MB, otherwise use direct upload
  const shouldUseMultipart = file.size > 500 * 1024 * 1024 // 500MB threshold

  if (shouldUseMultipart) {
    console.log(`📦 Using multipart upload for ${(file.size / 1024 / 1024).toFixed(1)}MB file`)
    const uploader = new MultipartUploader(file, onProgress)
    return await uploader.start()
  } else {
    console.log(`📄 Using direct upload for ${(file.size / 1024 / 1024).toFixed(1)}MB file`)
    // Fall back to existing direct upload method
    throw new Error('Direct upload fallback not implemented - use multipart for all files')
  }
}